<template>
  <!-- Box only. Arrangement (row/col) and top/bottom spacing are the
       page's responsibility (PageTrendRadar.vue) — same pattern as the
       other section components. -->
  <div class="card">
    <div class="card-header">
      <div class="row flex-between-center">
        <div class="col-auto">
          <h5 class="fs-6 mb-0">
            <span class="fas fa-flask me-1"></span>
            Forward Paper Book <span v-if="book">({{ book.bookId }})</span>
          </h5>
        </div>
        <div v-if="books.length > 1" class="col-auto">
          <select v-model="selectedBookId" class="form-select form-select-sm fs-10" @change="loadBook">
            <option v-for="b in books" :key="b.bookId" :value="b.bookId">{{ b.bookId }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="card-body p-0">
      <p v-if="loading" class="text-center py-4 text-muted mb-0">Loading…</p>
      <p v-else-if="error" class="text-center py-4 text-danger mb-0">{{ error }}</p>

      <template v-else-if="book">
        <!-- Snapshot header -->
        <div v-if="snapshot" class="px-3 py-2 border-bottom fs-10">
          <span class="fw-bold">NAV ${{ fmtMoney(snapshot.nav) }}</span>
          <span :class="pnlClass(snapshot.totalPnlPct)"> ({{ fmtPnl(snapshot.totalPnlPct) }})</span>
          <span class="text-700">
            since inception {{ book.inception }} ·
            Cash {{ snapshot.cashPct.toFixed(1) }}% ·
            {{ snapshot.nPositions }} open ·
            as of run {{ snapshot.lastRunDate }}
          </span>
        </div>
        <p v-else class="px-3 py-2 text-700 fs-10 mb-0">
          No Trend Radar runs since inception {{ book.inception }} yet.
        </p>

        <div v-if="positions.length > 0" class="table-responsive scrollbar">
          <table class="table table-hover table-sm table-striped fs-10 mb-0 overflow-hidden">
            <thead class="bg-200">
              <tr>
                <th class="text-900 align-middle white-space-nowrap">Asset</th>
                <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('pctHoldings')">
                  Holdings % <span v-html="getSortIcon('pctHoldings')"></span>
                </th>
                <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('entryDate')">
                  Entered <span v-html="getSortIcon('entryDate')"></span>
                </th>
                <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('exitDate')">
                  Exited <span v-html="getSortIcon('exitDate')"></span>
                </th>
                <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('pnlPct')">
                  P&amp;L % <span v-html="getSortIcon('pnlPct')"></span>
                </th>
              </tr>
            </thead>
            <tbody class="list">
              <tr v-for="p in sortedPositions" :key="p.venueSymbol + '|' + p.entryDate + '|' + (p.exitDate || 'open')">
                <td class="py-2 pe-2 align-middle white-space-nowrap">
                  <a :href="'https://www.tradingview.com/chart/?symbol=' + p.venueSymbol"
                     target="_blank" class="fw-bold text-primary">{{ p.venueSymbol }}</a>
                </td>
                <td class="py-2 pe-2 align-middle white-space-nowrap">
                  <span v-if="p.pctHoldings != null">{{ p.pctHoldings.toFixed(1) }}%</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="py-2 pe-2 align-middle white-space-nowrap">{{ p.entryDate }}</td>
                <td class="py-2 pe-2 align-middle white-space-nowrap">
                  <span v-if="p.exitDate">{{ p.exitDate }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="py-2 pe-2 align-middle white-space-nowrap" :class="pnlClass(p.pnlPct)">
                  {{ fmtPnl(p.pnlPct) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="snapshot" class="text-center py-4 text-muted">
          No positions yet.
        </div>

        <p class="px-3 py-2 mb-0 fs-10 text-700 border-top">{{ book.strategyDesc }}</p>
        <p class="px-3 pb-2 mb-0 fs-10 text-500">
          Paper book — modeled fills at the run close, 0.1%/side, no execution.
          State-based, grey-exit (differs from the Radar UI's Red-only trade view).
          Research, not advice.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/api/apiFetch'

export default {
  name: 'PaperBookPanel',
  data () {
    return {
      books: [],
      selectedBookId: null,
      book: null,
      snapshot: null,
      positions: [],
      sortBy: 'pctHoldings',
      sortDirection: 'desc',
      loading: true,
      error: null
    }
  },
  computed: {
    sortedPositions () {
      const sorted = [...this.positions]
      const field = this.sortBy
      const dir = this.sortDirection === 'asc' ? 1 : -1

      sorted.sort((a, b) => {
        let valA = a[field]
        let valB = b[field]

        // Nulls always sort to the bottom (closed lots have no
        // pctHoldings; open lots have no exitDate).
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1

        // Numeric sort for the % columns (coerce defensively).
        if (field === 'pctHoldings' || field === 'pnlPct') {
          return (Number(valA) - Number(valB)) * dir
        }

        // String/date sort (entryDate, exitDate are ISO strings).
        valA = String(valA).toLowerCase()
        valB = String(valB).toLowerCase()
        if (valA < valB) return -1 * dir
        if (valA > valB) return 1 * dir
        return 0
      })

      return sorted
    }
  },
  async mounted () {
    const baseUrl = process.env.VUE_APP_API_URL
    try {
      const res = await apiFetch(baseUrl + '/trendradar/paper-books', { method: 'GET' })
      if (!res.ok) { this.error = 'Paper books unavailable.'; this.loading = false; return }
      this.books = await res.json()
      if (!this.books.length) { this.loading = false; return }
      this.selectedBookId = this.books[0].bookId
      await this.loadBook()
    } catch (e) {
      this.error = 'Could not load paper books.'
      this.loading = false
    }
  },
  methods: {
    fmtMoney (n) {
      return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtPnl (v) {
      if (v == null) return '-'
      return (v >= 0 ? '+' : '') + Number(v).toFixed(2) + '%'
    },
    pnlClass (v) {
      if (v == null) return 'text-muted'
      return v >= 0 ? 'text-success' : 'text-danger'
    },
    sortTable (field) {
      if (this.sortBy === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortDirection = 'asc'
      }
    },
    getSortIcon (field) {
      if (this.sortBy !== field) return ''
      return this.sortDirection === 'asc' ? '&#9650;' : '&#9660;'
    },
    async loadBook () {
      this.loading = true
      this.error = null
      const baseUrl = process.env.VUE_APP_API_URL
      try {
        const res = await apiFetch(baseUrl + '/trendradar/paper-book/' + this.selectedBookId, { method: 'GET' })
        if (!res.ok) { this.error = 'Paper book unavailable.'; this.loading = false; return }
        const data = await res.json()
        this.book = data.book
        this.snapshot = data.book.lastRunDate != null ? {
          lastRunDate:  data.book.lastRunDate,
          nav:          data.book.nav,
          cash:         data.book.cash,
          cashPct:      data.book.cashPct,
          totalPnlPct:  data.book.totalPnlPct,
          nPositions:   data.book.nPositions,
        } : null
        this.positions = data.positions || []
      } catch (e) {
        this.error = 'Could not load this paper book.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Same sortable-header affordance as TrendRadarTable.vue (its rules are
   scoped to that component, so they have to be repeated here). */
.sortable {
  cursor: pointer;
  user-select: none;
}
.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
/* Same as TrendRadarTable: right-align the last column (P&L %). */
.table tr th:last-child {
  text-align: right;
}
.table tr td:last-child {
  text-align: right;
}
</style>
